// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * AgentCore Service - Streaming Response Handler
 *
 * Handles streaming responses from AgentCore agents using Server-Sent Events (SSE).
 *
 * CUSTOMIZATION FOR OTHER AGENT TYPES:
 * This service dynamically loads the appropriate parser based on the agent pattern.
 * The parser processes each streaming event. See strandsParser.js and langgraphParser.js for examples.
 * To support other agent frameworks, create a new parser file and update config.yaml.
 */

// Generate a UUID
const generateId = () => {
  return crypto.randomUUID();
}

// Configuration - will be populated from aws-exports.json
const AGENT_CONFIG = {
  AGENT_RUNTIME_ARN: "",
  AWS_REGION: "us-east-1",
  // OCI 等の自前ホスティングで chat 常駐へ直接届ける相対/絶対 URL（例 "/chat"）。
  // 設定時は AgentCore Runtime（ARN + AWS エンドポイント）の代わりに
  // `${CHAT_ENDPOINT}/invocations` へ POST する。未設定なら従来どおり AWS。
  CHAT_ENDPOINT: "",
}

let currentParser = null;

// Set configuration from environment or aws-exports
export const setAgentConfig = async (runtimeArn, region = "us-east-1", agentPattern = "strands-single-agent", chatEndpoint = "") => {
  AGENT_CONFIG.AGENT_RUNTIME_ARN = runtimeArn
  AGENT_CONFIG.AWS_REGION = region
  AGENT_CONFIG.CHAT_ENDPOINT = chatEndpoint || ""

  if (agentPattern === 'langgraph-single-agent') {
    currentParser = await import('./langgraphParser.js');
  } else {
    currentParser = await import('./strandsParser.js');
  }
}

// Maximum number of automatic retries for pre-stream connection errors.
// AgentCore Runtime is serverless and can take up to ~2 minutes to cold-start
// (see Dockerfile HEALTHCHECK --start-period=120s), and idle sessions are
// terminated after 15 minutes by default. Without enough retries, the very
// first request after an idle period fails with "Failed to get response:
// network error" before the container becomes healthy.
// 12 attempts × 10 seconds ≈ 120 seconds of patience, matching the cold
// start window. Retries only run during the connection phase (before the
// stream begins), so this never triggers duplicate agent executions.
// NOTE: 待ち時間は固定 10 秒。以前は 10s×(attempt+1) と漸増していたため、
// 総待機が約13分に膨らみ「考え中のまま無反応」に見えていた（2026-07-02 P3 修正）。
const MAX_RETRIES = 12;
const RETRY_DELAY_MS = 10000;

/**
 * Invokes the AgentCore runtime with streaming support.
 * Retries only on connection-phase network errors (before streaming begins).
 * Once the stream starts, errors are NOT retried to avoid duplicate agent executions.
 * onConnectionRetry(attempt, maxRetries) は接続リトライのたびに呼ばれる（UI が
 * 「エージェント起動中」を出すためのフック・省略可）。
 * onActivity(toolName|null) はツール実行の開始/終了で呼ばれる（実行インジケータ用・省略可）。
 * onHeartbeat() は SSE バイト受信のたびに呼ばれる（keepalive 含む・無音検知用・省略可）。
 *   ヘッダ受信（=ストリーム開始）時にも1回呼ぶ。
 *
 * 戻り値: { completion, turnEnded }
 *   turnEnded=false は「ストリームは終わったが turn_end マーカーが無い」= 途中切断。
 *   呼び出し側（ChatStreamContext）が自動再開の判定に使う（2026-07-17 切断対策）。
 */
export const invokeAgentCore = async (query, sessionId, onStreamUpdate, accessToken, userId, attachments = undefined, customInstructions = undefined, knowledgeFiles = undefined, enableExtendedThinking = false, signal = undefined, userEmail = undefined, onConnectionRetry = undefined, onActivity = undefined, onHeartbeat = undefined) => {
  try {
    if (!userId) {
      throw new Error("No valid user ID found in session. Please ensure you are authenticated.")
    }

    if (!accessToken) {
      throw new Error("No valid access token found. Please ensure you are authenticated.")
    }

    if (!AGENT_CONFIG.CHAT_ENDPOINT && !AGENT_CONFIG.AGENT_RUNTIME_ARN) {
      throw new Error("Agent Runtime ARN not configured")
    }

    // 接続先 URL:
    // - CHAT_ENDPOINT 設定時（OCI 等）: 同一オリジンのリバースプロキシ経由で
    //   chat 常駐へ（例 /chat/invocations）。イベント形は AgentCore と同一なので
    //   パーサは無改修で共用する。
    // - 未設定（AWS）: 従来どおり Bedrock AgentCore Runtime を ARN 指定で invoke。
    let url
    if (AGENT_CONFIG.CHAT_ENDPOINT) {
      url = `${AGENT_CONFIG.CHAT_ENDPOINT.replace(/\/$/, "")}/invocations`
    } else {
      const endpoint = `https://bedrock-agentcore.${AGENT_CONFIG.AWS_REGION}.amazonaws.com`
      const escapedAgentArn = encodeURIComponent(AGENT_CONFIG.AGENT_RUNTIME_ARN)
      url = `${endpoint}/runtimes/${escapedAgentArn}/invocations?qualifier=DEFAULT`
    }

    // Generate trace ID
    const traceId = `1-${Math.floor(Date.now() / 1000).toString(16)}-${generateId()}`

    // Set up headers
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      "X-Amzn-Trace-Id": traceId,
      "Content-Type": "application/json",
      "X-Amzn-Bedrock-AgentCore-Runtime-Session-Id": sessionId,
    }

    // Create the payload
    const payload = {
      prompt: query,
      runtimeSessionId: sessionId,
      userId: userId,
      ...(userEmail ? { userEmail } : {}),
      ...(attachments && attachments.length > 0 ? { attachments } : {}),
      ...(customInstructions ? { customInstructions } : {}),
      ...(knowledgeFiles && knowledgeFiles.length > 0 ? { knowledgeFiles } : {}),
      ...(enableExtendedThinking ? { enableExtendedThinking: true } : {}),
    }

    // --- Connection phase: retryable ---
    let response;
    let lastConnError;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        response = await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
          signal,
        })
        lastConnError = null;
        break; // connection succeeded
      } catch (connError) {
        if (connError.name === 'AbortError') throw connError;
        lastConnError = connError;
        if (attempt < MAX_RETRIES) {
          console.warn(`[AgentCore] Connection error on attempt ${attempt + 1}/${MAX_RETRIES + 1}, retrying...`, connError.message);
          try {
            onConnectionRetry?.(attempt + 1, MAX_RETRIES);
          } catch {
            /* UI 通知の失敗で本流を止めない */
          }
          await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
        }
      }
    }
    if (lastConnError) throw lastConnError;

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP ${response.status}: ${errorText}`)
    }

    // --- Streaming phase: NOT retryable (backend already processing) ---
    // Reset parser state for new stream
    if (currentParser.resetParserState) {
      currentParser.resetParserState();
    }

    // ヘッダ受信＝ストリーム開始。以降は 3 秒毎の keepalive を含め必ずバイトが流れる
    // ので、呼び出し側は短い無音しきい値に切り替えられる（2026-07-17 切断対策）。
    try {
      onHeartbeat?.();
    } catch {
      /* 生存通知の失敗で本流を止めない */
    }

    let completion = '';
    let buffer = '';

    // Handle streaming response
    if (response.body) {
      const reader = response.body.getReader()
      const decoder = new TextDecoder()

      try {
        while (true) {
          if (signal?.aborted) break
          const { done, value } = await reader.read()
          if (done) break

          // 受信バイト（keepalive 含む）はすべて生存信号として数える
          try {
            onHeartbeat?.();
          } catch {
            /* 生存通知の失敗で本流を止めない */
          }

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Process complete lines (SSE format uses newlines as delimiters)
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.trim()) {
              completion = currentParser.parseStreamingChunk(line, completion, onStreamUpdate, onActivity);
            }
          }
        }
      } finally {
        reader.releaseLock()
      }
    } else {
      // Fallback for non-streaming response
      completion = await response.text()
      onStreamUpdate(completion)
      // 全文を一括受信できた＝完了扱い（切断判定の対象外）
      return { completion, turnEnded: true }
    }

    // turn_end マーカー未受信のままストリームが終わった場合は途中切断とみなす。
    // マーカーに対応していないパーサ（sawTurnEnd 未実装）は常に完了扱い＝従来挙動。
    const turnEnded = currentParser.sawTurnEnd ? currentParser.sawTurnEnd() : true
    return { completion, turnEnded }
  } catch (error) {
    if (error.name === 'AbortError') {
      throw error
    }
    console.error("Error invoking AgentCore:", error)
    throw error
  }
}

/**
 * Generate a new session ID
 */
export const generateSessionId = () => {
  return generateId()
}
